import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FaLock,
  FaSignOutAlt,
  FaUniversity,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import logo from "../../assets/images/LOGO.png";
import { useDispatch, useSelector } from "react-redux";
import { clearUser, fetchUserRole } from "../../redux/slices/userSlice";
import Modal from "../Modal/Modal";
import { currencyOption } from "../../utility/data";
import { userProfileUpdate } from "../../services/userProfile/updateProfile";
import { Toast } from "primereact/toast";
import { FiSun, FiMoon } from "react-icons/fi";
import { handleErrorRedirect } from "../../utility/errorRedirect";
const ProfileDropdownMenu = ({
  isOpen,
  onProfileClick,
  onLogout,
  role,
  navigate,
  setProfileDropdownOpen,
}) => {
  if (!isOpen) return null;

  return (
    <ul className="absolute right-0 z-10 text-xs w-[9rem] bg-white border dark:border-primary border-lightPrimary shadow-lg rounded-lg py-[4px]">
      <li
        className="flex items-center p-1 hover:bg-gray-100 cursor-pointer"
        onClick={onProfileClick}
      >
        <FaUser className="mr-2 text-gray-800" />
        <span className="text-lightPrimary dark:text-primary-gradient font-bold">
          My Profile
        </span>
      </li>
      {role == "user" && (
        <>
          <li
            className="flex items-center p-1 text-gray-800 hover:bg-gray-100 cursor-pointer"
            onClick={() => navigate("/password-change")}
          >
            <FaLock className="mr-2" />
            <span className="text-lightPrimary dark:text-primary-gradient font-bold">
              Change Password
            </span>
          </li>
          <li
            className="flex items-center p-1 text-gray-800 hover:bg-gray-100 cursor-pointer"
            onClick={() => {
              navigate("/view-legacy");
              setProfileDropdownOpen(false);
            }}
          >
            <FaUniversity className="mr-2" />
            <span className="text-lightPrimary dark:text-primary-gradient font-bold">
              View Legacy
            </span>
          </li>
          <li
            className="flex items-center p-1 text-gray-800 hover:bg-gray-100 cursor-pointer"
            onClick={() => {
              navigate("/view-group");
              setProfileDropdownOpen(false);
            }}
          >
            <FaUsers className="mr-2" />
            <span className="text-lightPrimary dark:text-primary-gradient font-bold">
              View Group
            </span>
          </li>
        </>
      )}
      <li
        className="flex items-center p-1 hover:bg-gray-100 cursor-pointer text-red-500"
        onClick={onLogout}
      >
        <FaSignOutAlt className="mr-2" />
        <span className="font-bold">Log out</span>
      </li>
    </ul>
  );
};

const Header = ({ email, name, role, theme, toggleTheme }) => {
  const { currency } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const profileDropdownRef = useRef(null);
  const accessToken = JSON.parse(localStorage.getItem("access_token"));
  const toast = useRef(null);

  const dispatch = useDispatch();
  const toggleProfileDropdown = () => setProfileDropdownOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    dispatch(clearUser());
    navigate("/signin");
  };

  const handleProfileClick = () => {
    setIsModalOpen(true);
    setProfileDropdownOpen(false);
  };

  const handleProfileSave = (updatedProfile) => {
    updateUserProfile(updatedProfile);
  };

  const updateUserProfile = async (updatedProfile) => {
    setLoading(true);
    try {
      const { name, currency } = updatedProfile;
      const response = await userProfileUpdate(name, currency, accessToken);

      if (response.data.status === true) {
        await dispatch(fetchUserRole(accessToken)).unwrap();
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: response.data.message,
          life: 3000,
        });
        setTimeout(() => {
          setIsModalOpen(false);
        }, 500);
      }
    } catch (error) {
      const status = error?.response?.status;
      handleErrorRedirect(status, navigate);
      console.error("Error updating profile:", error);
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: error.message,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const profileFields = [
    { name: "name", label: "Name", value: name, disable: "false" },
    {
      name: "email",
      label: "Email",
      value: email,
      type: "email",
      disable: "true",
    },
    {
      name: "currency",
      label: "Default Currency",
      value: currency ? currency : "CHF",
      type: "select",
      options: currencyOption,
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <Toast ref={toast} />
      <header className="flex items-center justify-between fixed w-full header_bg z-50 p-4 shadow text-white h-[4.5rem] border-b border-[rgba(255,255,255,.67)]">
        <div className="flex items-center space-x-4">
          <Link to={"/"}>
            {" "}
            <img src={logo} alt="App Icon" className="w-[5rem] h-[3.7rem]" />
          </Link>
        </div>

        <div className="flex items-center">
          {role === "admin" && (
            <Link to={"/user-management"}>
              <button className="ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium bg-lightPrimary dark:text-black dark:button-primary-gradient rounded-md hover:bg-gray-100 focus:outline-none">
                <FaUser className="md:mr-2" />
                <span className="hidden sm:block">User Management</span>
              </button>
            </Link>
          )}

          <div className="relative">
            <div className="py-3 px-4 inline-flex items-center gap-x-4 text-sm font-medium rounded-lg text-gray-700 dark:text-white shadow-sm">
              <button
                onClick={toggleTheme}
                className="w-8 h-8 rounded-full p-1 flex items-center justify-center transition-colors duration-300"
              >
                <div className="w-6 h-6 flex items-center justify-center rounded-full transition-transform duration-300">
                  {theme !== "dark" ? (
                    <FiMoon className="w-6 h-6 text-info" />
                  ) : (
                    <FiSun className="w-6 h-6 text-yellow-800" />
                  )}
                </div>
              </button>

              <div className="flex-col items-start hidden sm:flex">
                <span className="text-base text-lightPrimary dark:text-primary-gradient font-bold">
                  {name ? name : ""}
                </span>
                <span className="text-xs truncate w-24 text-info dark:text-grey">
                  {email ? email : ""}
                </span>
              </div>
            </div>
          </div>
          <div className="relative" ref={profileDropdownRef}>
            <img
              onClick={toggleProfileDropdown}
              src="https://images.unsplash.com/photo-1633332755192-727a05c4013d"
              alt="Profile"
              className={`h-10 w-10 cursor-pointer rounded-full object-cover border ${
                profileDropdownOpen
                  ? "dark:border-primary border-lightPrimary"
                  : ""
              }`}
            />
            <ProfileDropdownMenu
              isOpen={profileDropdownOpen}
              onProfileClick={handleProfileClick}
              onLogout={handleLogout}
              role={role}
              navigate={navigate}
              setProfileDropdownOpen={setProfileDropdownOpen}
            />
          </div>
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fields={profileFields}
          onSave={handleProfileSave}
          loading={loading}
        />
      </header>
    </>
  );
};

export default Header;
